// src/components/RolePicker.tsx
// Searchable picker for a team role, in the same shape as UserPicker: a trigger
// button, a portalled dropdown with a search field, a line saying which set is
// on offer, and explicit loading / error / empty states.
//
// A role belongs to a team, so there is no single "list roles" call. The picker
// reads the teams, then each team's roles, and presents one flat list grouped
// under team headings — searching matches the role name, its description, or the
// team name, so "find the team first" and "search across teams" are the same
// gesture rather than two modes to choose between.
//
// Teams a person cannot read are skipped rather than failing the whole list
// (ListTeamRoles requires membership of the team) — which is why the dropdown
// says it is showing the teams you can see, and reports a failure only when no
// team could be read at all.

import { useState, useEffect, useRef, useMemo, useCallback, useId } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { useI18n } from '@react/hooks';
import { ListTeams } from '@core/api/endpoints/team/list_teams';
import { ListTeamRoles } from '@core/api/endpoints/team/list_team_roles';
import type { TeamRole } from '@core/types/team';

/** Roles of one team, kept together so every row can say where it comes from. */
interface RoleGroup {
  teamId: string;
  teamName: string;
  roles: TeamRole[];
}

export interface RolePickerProps {
  /** Selected role's entity id, or '' for none. */
  value: string;
  /** The full role comes with the id so callers can show it without a refetch. */
  onChange: (roleId: string, role?: TeamRole) => void;
  label?: string;
  placeholder?: string;
  /** Inline message under the field. Setting it moves focus to the trigger. */
  error?: string;
  className?: string;
  disabled?: boolean;
  id?: string;
}

const PAGE = (limit: number) => ({
  pagination: { limit, cursor: '', direction: 'forward' },
  sort: [],
});

export function RolePicker({
  value,
  onChange,
  label,
  placeholder,
  error,
  className = '',
  disabled,
  id,
}: RolePickerProps) {
  const { t } = useI18n();
  const errorId = `${useId()}-error`;

  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [groups, setGroups] = useState<RoleGroup[]>([]);
  const [teamCount, setTeamCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [nonce, setNonce] = useState(0);

  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});

  // The catalogue is read when the dropdown opens, and also when a value arrives
  // already selected — an Entity carries no name, so the only way to show a
  // saved role as anything other than a UUID is to look it up.
  const needsResolve = !!value && !loaded;
  const shouldLoad = isOpen || needsResolve;

  useEffect(() => {
    if (!shouldLoad || loaded) return;
    let cancelled = false;
    setLoading(true);
    setLoadError(false);

    (async () => {
      try {
        const teamsResponse = await ListTeams(PAGE(100));
        if (cancelled) return;
        const teams = teamsResponse?.teams?.teams ?? [];
        setTeamCount(teams.length);
        if (teams.length === 0) {
          setGroups([]);
          setLoaded(true);
          return;
        }

        const results = await Promise.all(
          teams.map(async (team): Promise<RoleGroup | null> => {
            const teamId = team.entity?.id ?? '';
            if (!teamId) return null;
            try {
              const response = await ListTeamRoles(teamId, PAGE(200));
              return {
                teamId,
                teamName: team.name || teamId,
                roles: response?.roles?.roles ?? [],
              };
            } catch {
              // Not a member of this team, or it disappeared — its roles are
              // simply not on offer; that is not a failure of the whole list.
              return null;
            }
          }),
        );
        if (cancelled) return;

        const readable = results.filter((group): group is RoleGroup => group !== null);
        if (readable.length === 0) {
          setLoadError(true);
          return;
        }
        setGroups(readable.filter((group) => group.roles.length > 0));
        setLoaded(true);
      } catch {
        if (!cancelled) setLoadError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldLoad, nonce]);

  const retry = () => {
    setLoaded(false);
    setLoadError(false);
    setNonce((n) => n + 1);
  };

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => searchRef.current?.focus(), 50);
      return () => clearTimeout(timer);
    }
    setSearch('');
  }, [isOpen]);

  // A message appearing under the field is the answer to a submit that was
  // refused; the field it belongs to takes focus so the person lands on it.
  const previousError = useRef<string | undefined>(error);
  useEffect(() => {
    if (error && !previousError.current) buttonRef.current?.focus();
    previousError.current = error;
  }, [error]);

  const updatePosition = useCallback(() => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const maxH = 340;
    const openAbove = spaceBelow < maxH && rect.top > spaceBelow;

    setDropdownStyle({
      position: 'fixed',
      left: rect.left,
      width: Math.max(rect.width, 300),
      ...(openAbove
        ? { bottom: window.innerHeight - rect.top + 4 }
        : { top: rect.bottom + 4 }),
      maxHeight: Math.min(maxH, openAbove ? rect.top - 8 : spaceBelow - 8),
    });
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    updatePosition();
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        containerRef.current && !containerRef.current.contains(target) &&
        dropdownRef.current && !dropdownRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };
    const handleScroll = () => updatePosition();
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      setIsOpen(false);
      buttonRef.current?.focus();
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleScroll);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleScroll);
    };
  }, [isOpen, updatePosition]);

  const filteredGroups = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return groups;
    return groups
      .map((group) => {
        // Typing a team name is how you narrow to a team, so a team match keeps
        // all of its roles rather than filtering them away again.
        if (group.teamName.toLowerCase().includes(query)) return group;
        return {
          ...group,
          roles: group.roles.filter(
            (role) =>
              (role.name || '').toLowerCase().includes(query) ||
              (role.description || '').toLowerCase().includes(query),
          ),
        };
      })
      .filter((group) => group.roles.length > 0);
  }, [groups, search]);

  const selected = useMemo(() => {
    if (!value) return null;
    for (const group of groups) {
      const role = group.roles.find((r) => r.entity?.id === value);
      if (role) return { role, group };
    }
    return null;
  }, [value, groups]);

  const handleSelect = (role: TeamRole) => {
    onChange(role.entity?.id || '', role);
    setIsOpen(false);
  };

  const swatch = (color: string) => color || 'hsl(var(--muted-foreground))';

  const hasAnyRole = groups.length > 0;
  const triggerText = selected
    ? selected.role.name
    : !loaded && !loadError
      ? t('common.loading')
      : t('rolePicker.unknownRole');

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      {label && (
        <label htmlFor={id} className="block text-sm font-medium text-foreground mb-1.5">
          {label}
        </label>
      )}

      <div className="relative">
        <button
          ref={buttonRef}
          id={id}
          type="button"
          disabled={disabled}
          onClick={() => !disabled && setIsOpen((prev) => !prev)}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-describedby={error ? errorId : undefined}
          className={`
            w-full flex items-center gap-2 rounded-md border px-3 py-2 text-left text-sm transition-colors
            ${error ? 'border-destructive focus:ring-destructive' : 'border-input focus:ring-ring'}
            ${isOpen ? 'ring-2 ring-ring' : ''}
            ${value ? 'pr-9' : ''}
            bg-card text-foreground hover:bg-muted/50
            focus:outline-none focus-visible:ring-2 focus-visible:ring-ring
            disabled:opacity-50 disabled:cursor-not-allowed
          `}
        >
          {value ? (
            <>
              <span
                aria-hidden="true"
                className="h-2.5 w-2.5 rounded-full flex-shrink-0"
                style={{ backgroundColor: swatch(selected?.role.color || '') }}
              />
              <span className="flex-1 min-w-0">
                <span className="block text-sm font-medium text-foreground truncate leading-tight">
                  {triggerText}
                </span>
                {selected && (
                  <span className="block text-xs text-muted-foreground truncate leading-tight">
                    {selected.group.teamName}
                  </span>
                )}
              </span>
            </>
          ) : (
            <>
              <svg
                className="h-4 w-4 text-muted-foreground flex-shrink-0"
                viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}
                strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
              >
                <path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M22 21v-2a4 4 0 00-3-3.87" />
              </svg>
              <span className="flex-1 text-muted-foreground truncate">
                {placeholder || t('rolePicker.placeholder')}
              </span>
              <svg
                className={`h-4 w-4 text-muted-foreground flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}
                strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
              >
                <path d="M6 9l6 6 6-6" />
              </svg>
            </>
          )}
        </button>

        {value && !disabled && (
          <button
            type="button"
            onClick={() => onChange('', undefined)}
            aria-label={t('common.clear')}
            title={t('common.clear')}
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded-sm p-0.5 text-muted-foreground transition-colors hover:text-destructive focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <svg
              className="h-4 w-4"
              viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}
              strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {error && (
        <p id={errorId} className="mt-1 text-xs text-destructive" role="alert">{error}</p>
      )}

      {isOpen && createPortal(
        <div
          ref={dropdownRef}
          style={dropdownStyle}
          className="z-50 flex flex-col rounded-md border border-border bg-popover shadow-lg overflow-hidden"
        >
          <div className="p-2 border-b border-border flex-shrink-0">
            <div className="relative">
              <svg
                className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground"
                viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}
                strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
              >
                <circle cx="11" cy="11" r="8" />
                <path d="M21 21l-4.35-4.35" />
              </svg>
              <input
                ref={searchRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label={t('rolePicker.searchPlaceholder')}
                className="w-full rounded-md border border-input bg-background pl-8 pr-3 py-1.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                placeholder={t('rolePicker.searchPlaceholder')}
              />
            </div>
          </div>

          {/* Which set of roles is on offer — never left to be guessed. */}
          {hasAnyRole && (
            <p className="border-b border-border px-3 py-1.5 text-xs text-muted-foreground flex-shrink-0">
              {t('rolePicker.fromTeams')}
            </p>
          )}

          <div className="overflow-y-auto py-1">
            {loading ? (
              <div className="px-3 py-4 text-center">
                <div className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                <p className="text-xs text-muted-foreground mt-1">{t('common.loading')}</p>
              </div>
            ) : loadError ? (
              <div className="px-3 py-4 text-center">
                <p className="text-sm text-destructive">{t('rolePicker.loadFailed')}</p>
                <button
                  type="button"
                  onClick={retry}
                  className="mt-1 rounded-sm text-xs font-medium text-primary underline-offset-2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {t('common.retry')}
                </button>
              </div>
            ) : !hasAnyRole ? (
              <div className="px-3 py-4 text-center">
                <p className="text-sm text-muted-foreground">
                  {teamCount === 0 ? t('rolePicker.noTeams') : t('rolePicker.noRoles')}
                </p>
                <Link
                  to="/teams"
                  target="_blank"
                  className="mt-1 inline-block rounded-sm text-xs font-medium text-primary underline-offset-2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {teamCount === 0 ? t('rolePicker.createTeam') : t('rolePicker.manageRoles')}
                </Link>
              </div>
            ) : filteredGroups.length === 0 ? (
              <div className="px-3 py-4 text-center">
                <p className="text-sm text-muted-foreground">{t('common.noResults')}</p>
              </div>
            ) : (
              <>
                {value && (
                  <button
                    type="button"
                    onClick={() => { onChange('', undefined); setIsOpen(false); }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-muted transition-colors"
                  >
                    <svg
                      className="h-4 w-4 text-muted-foreground flex-shrink-0"
                      viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}
                      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
                    >
                      <path d="M18 6L6 18M6 6l12 12" />
                    </svg>
                    <span className="text-sm text-muted-foreground">{t('rolePicker.clear')}</span>
                  </button>
                )}

                {filteredGroups.map((group) => (
                  <div key={group.teamId}>
                    <p className="px-3 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                      {group.teamName}
                    </p>
                    {group.roles.map((role) => {
                      const roleId = role.entity?.id || '';
                      const isSelected = roleId === value;
                      return (
                        <button
                          key={roleId}
                          type="button"
                          onClick={() => handleSelect(role)}
                          className={`w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors ${isSelected ? 'bg-primary/5' : 'hover:bg-muted'}`}
                        >
                          <span
                            aria-hidden="true"
                            className="h-2.5 w-2.5 rounded-full flex-shrink-0"
                            style={{ backgroundColor: swatch(role.color) }}
                          />
                          <span className="flex-1 min-w-0">
                            <span className="flex items-center gap-1.5">
                              <span className="text-sm font-medium text-foreground truncate">{role.name}</span>
                              {isSelected && (
                                <svg
                                  className="h-4 w-4 text-primary flex-shrink-0"
                                  viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}
                                  strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
                                >
                                  <path d="M20 6L9 17l-5-5" />
                                </svg>
                              )}
                            </span>
                            {role.description && (
                              <span className="block text-xs text-muted-foreground truncate">{role.description}</span>
                            )}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                ))}
              </>
            )}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}

export default RolePicker;
